import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getLanguageMeta, type SupportedLanguage } from "@/i18n/config";
import { translateTextsWithGoogle } from "@/i18n/googleTranslateService";

const defaultLanguage: SupportedLanguage = "pt-BR";
const maxTextsPerRequest = 50;
const maxTextLength = 5000;
const cachePrefix = "geoanalytics.autoTranslate.v1";
const translatableAttributes = ["aria-label", "aria-description", "title", "placeholder", "alt"];
const noTranslateSelector = [
  "[data-geo-no-auto-translate]",
  "[translate='no']",
  ".notranslate",
  "script",
  "style",
  "noscript",
  "template",
  "svg",
  "canvas",
  "video",
  "audio",
  "code",
  "pre",
  "[contenteditable='true']"
].join(",");
const textNodeNoTranslateSelector = [
  noTranslateSelector,
  "input",
  "textarea",
  "select",
  "option"
].join(",");
const translatedTextHostSelector = [
  ".MuiTypography-root",
  ".MuiButton-root",
  ".MuiChip-label",
  ".MuiMenuItem-root",
  ".MuiTab-root",
  ".MuiAlert-message",
  ".MuiFormLabel-root",
  "a",
  "button",
  "label",
  "legend",
  "li",
  "p",
  "span",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6"
].join(",");

type TranslatableAttribute = (typeof translatableAttributes)[number];

type TranslationTarget =
  | {
      kind: "text";
      node: Text;
      original: string;
      source: string;
    }
  | {
      kind: "attribute";
      element: Element;
      attribute: TranslatableAttribute;
      original: string;
      source: string;
    };

type TranslatedElementOriginalAttributes = {
  dir: string | null;
  lang: string | null;
  marker: string | null;
};

const textOriginals = new WeakMap<Text, string>();
const attributeOriginals = new WeakMap<Element, Map<TranslatableAttribute, string>>();
const translatedElementOriginalAttributes = new WeakMap<
  Element,
  TranslatedElementOriginalAttributes
>();
const memoryCache = new Map<string, string>();
const hasLetter = /\p{L}/u;

function hashText(value: string) {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return (hash >>> 0).toString(36);
}

function getCacheKey(targetLanguage: SupportedLanguage, source: string) {
  return `${cachePrefix}:${targetLanguage}:${source.length}:${hashText(source)}`;
}

function readCachedTranslation(targetLanguage: SupportedLanguage, source: string) {
  const key = getCacheKey(targetLanguage, source);
  const memoryValue = memoryCache.get(key);

  if (memoryValue) {
    return memoryValue;
  }

  try {
    const storedValue = window.localStorage.getItem(key);

    if (!storedValue) {
      return null;
    }

    const parsed = JSON.parse(storedValue) as { source?: string; translated?: string };

    if (parsed.source === source && typeof parsed.translated === "string") {
      memoryCache.set(key, parsed.translated);
      return parsed.translated;
    }
  } catch {
    return null;
  }

  return null;
}

function writeCachedTranslation(
  targetLanguage: SupportedLanguage,
  source: string,
  translated: string
) {
  const key = getCacheKey(targetLanguage, source);
  memoryCache.set(key, translated);

  try {
    window.localStorage.setItem(key, JSON.stringify({ source, translated }));
  } catch {
    // Local storage can be unavailable or full; in-memory cache still avoids duplicate calls.
  }
}

function normalizeSourceText(value: string) {
  return value.trim().replace(/\s+/g, " ");
}

function isProbablyTranslatable(value: string) {
  const source = normalizeSourceText(value);

  if (source.length < 2 || source.length > maxTextLength) {
    return false;
  }

  if (!hasLetter.test(source)) {
    return false;
  }

  if (/^https?:\/\//i.test(source) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(source)) {
    return false;
  }

  return true;
}

function withOriginalWhitespace(original: string, translated: string) {
  const prefix = original.match(/^[\s.,;:!?()[\]{}"']*/)?.[0] ?? "";
  const suffix = original.match(/\s*$/)?.[0] ?? "";

  return `${prefix}${translated}${suffix}`;
}

function decodeHtmlEntities(value: string) {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;

  return textarea.value;
}

function getAttributeOriginal(element: Element, attribute: TranslatableAttribute, value: string) {
  let originals = attributeOriginals.get(element);

  if (!originals) {
    originals = new Map();
    attributeOriginals.set(element, originals);
  }

  if (!originals.has(attribute)) {
    originals.set(attribute, value);
  }

  return originals.get(attribute) ?? value;
}

function restoreTextNodes(root: ParentNode) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();

  while (node) {
    const textNode = node as Text;
    const original = textOriginals.get(textNode);

    if (original && textNode.nodeValue !== original) {
      textNode.nodeValue = original;
    }

    node = walker.nextNode();
  }
}

function restoreAttributes(root: ParentNode) {
  const selector = translatableAttributes.map((attribute) => `[${attribute}]`).join(",");
  const elements = Array.from(root.querySelectorAll(selector));

  elements.forEach((element) => {
    const originals = attributeOriginals.get(element);

    if (!originals) {
      return;
    }

    originals.forEach((original, attribute) => {
      if (element.getAttribute(attribute) !== original) {
        element.setAttribute(attribute, original);
      }
    });
  });
}

function restoreTranslatedElementAttributes(root: ParentNode) {
  const elements = Array.from(root.querySelectorAll("[data-geo-auto-translated]"));

  elements.forEach((element) => {
    const originalAttributes = translatedElementOriginalAttributes.get(element);

    if (!originalAttributes) {
      element.removeAttribute("data-geo-auto-translated");
      return;
    }

    if (originalAttributes.dir === null) {
      element.removeAttribute("dir");
    } else {
      element.setAttribute("dir", originalAttributes.dir);
    }

    if (originalAttributes.lang === null) {
      element.removeAttribute("lang");
    } else {
      element.setAttribute("lang", originalAttributes.lang);
    }

    if (originalAttributes.marker === null) {
      element.removeAttribute("data-geo-auto-translated");
    } else {
      element.setAttribute("data-geo-auto-translated", originalAttributes.marker);
    }
  });
}

function collectTextTargets(root: ParentNode) {
  const targets: TranslationTarget[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;

      if (!parent || parent.closest(textNodeNoTranslateSelector)) {
        return NodeFilter.FILTER_REJECT;
      }

      return isProbablyTranslatable(node.nodeValue ?? "")
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    }
  });
  let node = walker.nextNode();

  while (node) {
    const textNode = node as Text;
    const current = textNode.nodeValue ?? "";
    const original = textOriginals.get(textNode) ?? current;

    if (!textOriginals.has(textNode)) {
      textOriginals.set(textNode, original);
    }

    targets.push({
      kind: "text",
      node: textNode,
      original,
      source: normalizeSourceText(original)
    });

    node = walker.nextNode();
  }

  return targets;
}

function collectAttributeTargets(root: ParentNode) {
  const selector = translatableAttributes.map((attribute) => `[${attribute}]`).join(",");
  const targets: TranslationTarget[] = [];
  const elements = Array.from(root.querySelectorAll(selector));

  elements.forEach((element) => {
    if (element.closest(noTranslateSelector)) {
      return;
    }

    translatableAttributes.forEach((attribute) => {
      const current = element.getAttribute(attribute);

      if (!current || !isProbablyTranslatable(current)) {
        return;
      }

      const original = getAttributeOriginal(element, attribute, current);

      targets.push({
        kind: "attribute",
        element,
        attribute,
        original,
        source: normalizeSourceText(original)
      });
    });
  });

  return targets;
}

function rememberTranslatedElement(element: Element) {
  if (translatedElementOriginalAttributes.has(element)) {
    return;
  }

  translatedElementOriginalAttributes.set(element, {
    dir: element.getAttribute("dir"),
    lang: element.getAttribute("lang"),
    marker: element.getAttribute("data-geo-auto-translated")
  });
}

function markTranslatedElement(element: Element, targetLanguage: SupportedLanguage) {
  const language = getLanguageMeta(targetLanguage);

  rememberTranslatedElement(element);
  element.setAttribute("data-geo-auto-translated", "true");
  element.setAttribute("dir", "auto");
  element.setAttribute("lang", language.code);
}

function getTranslatedTextHost(element: Element) {
  return element.closest(translatedTextHostSelector) ?? element;
}

function applyTranslation(
  target: TranslationTarget,
  translated: string,
  targetLanguage: SupportedLanguage
) {
  if (target.kind === "text") {
    if (target.node.isConnected) {
      target.node.nodeValue = withOriginalWhitespace(target.original, translated);

      if (target.node.parentElement) {
        markTranslatedElement(getTranslatedTextHost(target.node.parentElement), targetLanguage);
      }
    }

    return;
  }

  if (target.element.isConnected) {
    target.element.setAttribute(target.attribute, translated);
    markTranslatedElement(target.element, targetLanguage);
  }
}

async function translateTargets(
  targets: TranslationTarget[],
  targetLanguage: SupportedLanguage,
  shouldContinue: () => boolean,
  applySafely: (callback: () => void) => void
) {
  const pendingSources: string[] = [];
  const targetsBySource = new Map<string, TranslationTarget[]>();
  const resolvedTranslations = new Map<string, string>();

  targets.forEach((target) => {
    const cached = readCachedTranslation(targetLanguage, target.source);

    if (cached) {
      resolvedTranslations.set(target.source, cached);
      return;
    }

    if (!targetsBySource.has(target.source)) {
      targetsBySource.set(target.source, []);
      pendingSources.push(target.source);
    }

    targetsBySource.get(target.source)?.push(target);
  });

  for (let index = 0; index < pendingSources.length; index += maxTextsPerRequest) {
    if (!shouldContinue()) {
      return;
    }

    const chunk = pendingSources.slice(index, index + maxTextsPerRequest);
    const translations = await translateTextsWithGoogle(chunk, targetLanguage);

    chunk.forEach((source, sourceIndex) => {
      const translated = decodeHtmlEntities(translations[sourceIndex] ?? source);
      resolvedTranslations.set(source, translated);
      writeCachedTranslation(targetLanguage, source, translated);
    });
  }

  if (!shouldContinue()) {
    return;
  }

  applySafely(() => {
    targets.forEach((target) => {
      const translated = resolvedTranslations.get(target.source);

      if (translated) {
        applyTranslation(target, translated, targetLanguage);
      }
    });
  });
}

export function AutoTranslate() {
  const { i18n } = useTranslation();
  const runIdRef = useRef(0);
  const applyingRef = useRef(false);
  const targetLanguage = getLanguageMeta(i18n.language).code;

  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof window.setTimeout> | null = null;

    const root = () => document.body;
    const applySafely = (callback: () => void) => {
      applyingRef.current = true;

      try {
        callback();
      } finally {
        window.setTimeout(() => {
          applyingRef.current = false;
        }, 0);
      }
    };
    const runTranslation = async () => {
      if (disposed || !root()) {
        return;
      }

      const runId = runIdRef.current + 1;
      runIdRef.current = runId;

      if (targetLanguage === defaultLanguage) {
        applySafely(() => {
          restoreTextNodes(root());
          restoreAttributes(root());
          restoreTranslatedElementAttributes(root());
        });
        return;
      }

      const targets = [...collectTextTargets(root()), ...collectAttributeTargets(root())];

      if (!targets.length) {
        return;
      }

      try {
        await translateTargets(
          targets,
          targetLanguage,
          () => !disposed && runIdRef.current === runId,
          applySafely
        );
      } catch (error) {
        console.warn("GeoAnalytics automatic translation failed.", error);
      }
    };
    const scheduleTranslation = () => {
      if (disposed) {
        return;
      }

      if (timer) {
        window.clearTimeout(timer);
      }

      timer = window.setTimeout(() => {
        void runTranslation();
      }, 180);
    };
    const observer = new MutationObserver(() => {
      if (!applyingRef.current) {
        scheduleTranslation();
      }
    });

    observer.observe(root(), {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: translatableAttributes
    });
    scheduleTranslation();

    return () => {
      disposed = true;
      runIdRef.current += 1;
      observer.disconnect();

      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [targetLanguage]);

  return null;
}
